import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from '../authentication/auth.service';
import { AuthAction } from '../authentication/auth-action';
import { AccountService } from '../services/account.service';
import { NotificationRepo } from './notification.repo';
import { Notification, NotificationType } from './notification';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilKeyChanged, filter, map, switchMap, tap, takeUntil } from 'rxjs/operators';
import { PusherService } from './pusher.service';
import OneSignal from 'onesignal-cordova-plugin';
import { Account } from '../models/account';

@Injectable({
    providedIn: 'root',
})
export class NotificationService implements OnDestroy {
    private readonly notificationsBS$ = new BehaviorSubject<Notification[]>([]);
    notifications$ = this.notificationsBS$.asObservable();
    unreadNotifications$ = this.notifications$.pipe(
        map((notifications) => notifications.filter((notification) => !notification.read))
    );

    private readonly destroy$ = new Subject<void>();
    private onesignalInitialized = false;

    constructor(
        private authService: AuthService,
        private accountService: AccountService,
        private notificationRepo: NotificationRepo,
        private pusherService: PusherService
    ) {
        this.pusherService.onPusherEvent$
            .pipe(
                filter((x) => x.type !== NotificationType.EndOfDay),
                switchMap(() => this.fetchNotifications()),
                takeUntil(this.destroy$)
            )
            .subscribe({
                error: (err) => console.error('Pusher error:', err),
            });

        this.accountService.account$
            .pipe(
                filter((account): account is Account => !!account),
                distinctUntilKeyChanged('id'),
                switchMap(() => this.fetchNotifications()),
                takeUntil(this.destroy$)
            )
            .subscribe({
                error: (err) => console.error('Account fetch error:', err),
            });


        this.authService.onAuthChanged
            .pipe(
                filter((authAction) => authAction === AuthAction.Logout),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                OneSignal.Notifications.clearAll();
                OneSignal.logout();
            });
    }

    initNotifications(): void {
        if (!this.onesignalInitialized) {
            OneSignal.initialize(environment.oneSignalAppId);
            OneSignal.Location.setShared(false);

            // Add notification click listener only once
            OneSignal.Notifications.addEventListener('click', () => {
                this.fetchNotifications().subscribe({
                    error: (err) => console.error('Failed to fetch on click:', err),
                });
            });

            this.onesignalInitialized = true;
        }

        this.accountService.account$
            .pipe(
                filter((account): account is Account => !!account),
                distinctUntilKeyChanged('id'),
                takeUntil(this.destroy$))
            .subscribe((account) => {
                this.fetchNotifications().subscribe({
                    error: (err) => console.error('Notification fetch failed:', err),
                });

                OneSignal.logout();
                OneSignal.login(`courier_${account.id}`);
            });
    }

    fetchNotifications(): Observable<Notification[]> {
        return this.notificationRepo.getNotifications().pipe(
            tap((notifications) => this.notificationsBS$.next(notifications))
        );
    }

    setNotificationsRead(notificationIds: string[]): Observable<unknown> {
        return this.notificationRepo.setNotificationsRead(notificationIds);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
